import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { config } from '../config';
import { SurveyPasswordGate } from './SurveyPasswordGate';
import { boardDirectorUpdatedRoleQuestions } from './boardDirectorUpdatedRoleQuestions';
import type { SurveyQuestion } from './types';
import './survey.css';

const SurveyQuestionRenderer: React.FC<{
  question: SurveyQuestion;
  value: string | string[] | number | undefined;
  onChange: (value: string | string[] | number) => void;
}> = ({ question, value, onChange }) => {
  const id = `survey-q-${question.id}`;

  switch (question.type) {
    case 'text':
    case 'email':
      return (
        <input
          id={id}
          type={question.type}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          className="survey-input"
        />
      );
    case 'textarea':
      return (
        <textarea
          id={id}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          className="survey-textarea"
          rows={4}
        />
      );
    case 'radio':
      return (
        <div className="survey-options" role="radiogroup" aria-labelledby={`${id}-label`}>
          {(question.options ?? []).map((opt) => (
            <label key={opt.value} className="survey-option">
              <input
                type="radio"
                name={question.id}
                value={opt.value}
                checked={value === opt.value}
                onChange={() => onChange(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      );
    case 'checkbox': {
      const checked = Array.isArray(value) ? (value as string[]) : [];
      const toggle = (v: string) => {
        const next = checked.includes(v) ? checked.filter((x) => x !== v) : [...checked, v];
        onChange(next);
      };
      return (
        <div className="survey-options" role="group" aria-labelledby={`${id}-label`}>
          {(question.options ?? []).map((opt) => (
            <label key={opt.value} className="survey-option">
              <input
                type="checkbox"
                value={opt.value}
                checked={checked.includes(opt.value)}
                onChange={() => toggle(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      );
    }
    default:
      return (
        <input
          id={id}
          type="text"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="survey-input"
        />
      );
  }
};

const BoardDirectorUpdatedRoleContent: React.FC = () => {
  const [responses, setResponses] = useState<Record<string, string | string[] | number>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const setAnswer = useCallback((questionId: string, value: string | string[] | number) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitError(null);

      const hasRequired = boardDirectorUpdatedRoleQuestions.every((q) => {
        if (!q.required || q.type === 'info') return true;
        const v = responses[q.id];
        if (Array.isArray(v)) return v.length > 0;
        return v !== undefined && String(v).trim() !== '';
      });
      if (!hasRequired) {
        setSubmitError('Please answer the required question before submitting.');
        return;
      }

      setSubmitting(true);
      try {
        const payload = {
          survey_slug: 'board-director-updated-role',
          responses: Object.entries(responses).map(([question_id, value]) => ({
            question_id,
            value,
          })),
        };
        await axios.post(`${config.BACKEND_URL}/api/surveys/submit`, payload);
        setSubmitted(true);
      } catch (err: any) {
        console.error('Survey submission error:', err);
        setSubmitError(
          err?.response?.data?.detail ||
          'There was a problem submitting your survey. Please try again.'
        );
      } finally {
        setSubmitting(false);
      }
    },
    [responses]
  );

  if (submitted) {
    return (
      <div className="survey-page">
        <div className="survey-card survey-thanks">
          <h1>Thank you, Todd</h1>
          <p>
            Your response has been recorded. I truly appreciate you taking the time — it means a lot
            to me and to OARA. I will follow up shortly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-page">
      <div className="survey-card">
        <header className="survey-header">
          <div className="survey-org-label">OARA Youth Innovation Lab</div>
          <h1>Board Director — Updated Role Survey</h1>
          <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 1rem' }}>
            April 2026 | Confidential | For Todd Embley
          </p>
          <div className="survey-intro-box">
            <p>Hi Todd,</p>
            <p>Thank you again for the honest conversation we had earlier.</p>
            <p>
              OARA is restructuring its governance model. Effective from this board cycle I am
              proposing:
            </p>
            <ul>
              <li>
                All board directors serve as volunteers. The role is focused on oversight, vision,
                and governance guidance only.
              </li>
              <li>
                I am proposing that a formal <strong>Executive Director</strong> position be created.
                I (Renjith) will serve as volunteer ED on an ongoing basis. The ED handles all day-to-day
                operations, program delivery, grant management, HR, and administration, so the board
                is not expected to carry operational weight.
              </li>
              <li>
                The board meets <strong>once a month</strong>, virtually or in person, to review
                progress and provide direction. We have been meeting more frequently recently, but
                monthly is the expected standing cadence once we streamline things.
              </li>
              <li>
                I am proposing to transition the Secretary role to another director at this meeting.
                Your role as Voting Director at Large would be purely governance: oversight, monthly
                meetings, and major decisions.
              </li>
            </ul>
            <p style={{ marginTop: '1rem', fontWeight: 600, color: '#1e3a5f' }}>
              Why This Matters for You
            </p>
            <p>
              When you indicated in March that you wanted to step back, I believe a significant part
              of that was the operational and administrative load, not a lack of care for OARA's
              mission. If the role had looked like this from the start, your answer might have been
              different.
            </p>
            <p>I want to give you the chance to reconsider.</p>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="survey-form">
          {boardDirectorUpdatedRoleQuestions.map((q) => {
            if (q.type === 'info') {
              return (
                <div key={q.id} className="survey-info-block">
                  <p>{q.label}</p>
                </div>
              );
            }
            return (
              <div key={q.id} className="survey-question">
                <label id={`survey-q-${q.id}-label`} className="survey-question-label">
                  {q.label}
                  {q.required && <span className="survey-required"> *</span>}
                </label>
                <SurveyQuestionRenderer
                  question={q}
                  value={responses[q.id]}
                  onChange={(v) => setAnswer(q.id, v)}
                />
              </div>
            );
          })}
          {submitError && (
            <div className="survey-submit-error">{submitError}</div>
          )}
          <div className="survey-actions">
            <button type="submit" className="survey-submit-btn" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BoardDirectorUpdatedRoleSurvey: React.FC = () => {
  return (
    <SurveyPasswordGate surveySlug="boardDirectorUpdatedRole" title="Board Director — Updated Role">
      <BoardDirectorUpdatedRoleContent />
    </SurveyPasswordGate>
  );
};

export default BoardDirectorUpdatedRoleSurvey;
